import { Typography } from '@recruit-robin/robin-theme/web-components';
import { Edit_03, Star_01, Star_06, VerifiedSolid } from '@recruit-robin/robin-theme/web-icons';
import { motion } from 'motion/react';

import { SerializedStyles } from '@emotion/react';
import { BadgeColor, badgeVerifiedVariants, useStyles } from './Badge.styles';

const BadgeBase = ({
  children,
  size = 'medium',
  color = 'gray',
  customCss,
}: {
  children: React.ReactNode;
  size?: 'medium' | 'large';
  color?: BadgeColor;
  customCss?: SerializedStyles;
}) => {
  const classes = useStyles();

  return (
    <Typography
      customCss={[classes.badge, classes.size[size], classes.color[color], customCss]}
      variant={(v) => v.supporting['label-tiny']}>
      {children}
    </Typography>
  );
};

const BadgeInfoVerified = ({ children }: { children: React.ReactNode }) => {
  const classes = useStyles();

  return (
    <motion.div css={[classes.badge, classes.size.medium, classes.color.highlight]} variants={badgeVerifiedVariants}>
      <Typography variant={(v) => v.supporting['label-tiny']} inline>
        {children}
      </Typography>
    </motion.div>
  );
};

const BadgeInfo = ({ children, isVerified = false }: { children: React.ReactNode; isVerified?: boolean }) => {
  if (isVerified) {
    return <BadgeInfoVerified>{children}</BadgeInfoVerified>;
  }

  if (typeof children === 'string') {
    return (
      <BadgeBase size="medium" color="gray">
        <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{children}</span>
      </BadgeBase>
    );
  }

  return (
    <BadgeBase size="medium" color="gray">
      {children}
    </BadgeBase>
  );
};

const BadgeUpdatedAt = ({ children, type }: { children: React.ReactNode; type: 'new' | 'updated' }) => {
  return (
    <BadgeBase size="large" color={type === 'new' ? 'success' : 'info'}>
      {type === 'new' && <Star_01 size={16} />}
      {type === 'updated' && <Edit_03 size={16} />}
      {children}
    </BadgeBase>
  );
};

const BadgeVerified = ({ children }: { children: React.ReactNode }) => {
  return (
    <BadgeBase size="large" color="highlight-bg">
      <motion.span
        style={{ display: 'flex', alignItems: 'center', transform: 'translateX(0px)' }}
        variants={{ 'card-hover': { transform: 'translateX(-1px)', transition: { delay: 0.2 } } }}>
        <VerifiedSolid size={16} />
      </motion.span>
      <motion.span
        style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
        variants={{
          'card-hover': {
            marginLeft: '-6px',
            marginRight: '-6px',
            width: 0,
          },
        }}>
        {children}
      </motion.span>
    </BadgeBase>
  );
};

const BadgeMatchRate = ({ children, customCss }: { children: React.ReactNode; customCss?: SerializedStyles }) => {
  return (
    <BadgeBase size="large" color="match-rate" customCss={customCss}>
      <Star_06 size={16} />

      <Typography variant={(v) => v.supporting.label} inline>
        {children}
      </Typography>
    </BadgeBase>
  );
};

export const Badge = {
  UpdatedAt: BadgeUpdatedAt,
  Info: BadgeInfo,
  Verified: BadgeVerified,
  MatchRate: BadgeMatchRate,
};
